import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatchApp } from "../lib/redux";
import {
  startGetScheduledContents,
  startListenerScheduledContents,
} from "../actions/user";
import {
  META,
  TIKTOK,
  YOUTUBE,
  INSTAGRAM,
  STATES,
  FACEBOOK,
  LINKEDIN,
  INSTAGRAM_CONTENT_TYPE_STORIES,
  INSTAGRAM_CONTENT_TYPE_REELS,
  FACEBOOK_CONTENT_TYPE_STORIES,
  FACEBOOK_CONTENT_TYPE_REELS,
} from "../utils/constants";
import { GRADIENT_BLACK_COLOR } from "../utils/colors";
import { off } from "firebase/database";
import { useTranslationApp } from "../lib/i18next";
import { startOfMonth, startOfDay } from "date-fns";
import {
  CALENDAR_POST_TITLE,
  CALENDAR_STORIES_TITLE,
  CALENDAR_REELS_TITLE,
  CALENDAR_MULTIPLE_POST_TITLE,
  TIKTOK as TIKTOK_KEY,
  YOUTUBE as YOUTUBE_KEY,
  INSTAGRAM as INSTAGRAM_KEY,
  FACEBOOK as FACEBOOK_KEY,
  LINKEDIN as LINKEDIN_KEY,
  EVENT_DETAILS_NETWORK_FACEBOOK,
  EVENT_DETAILS_NETWORK_LINKEDIN,
  EVENT_DETAILS_NETWORK_TIKTOK,
  EVENT_DETAILS_NETWORK_YOUTUBE,
  EVENT_DETAILS_NETWORK_INSTAGRAM,
} from "../i18n/keysTranslations";
import {
  STATUS_CONFIG,
  classifyCalendarPublishIssue,
  resolveCalendarEventStatus,
} from "../utils/calendarEventPublishStatus";

const EVENT_DURATION_MS = 10 * 60 * 1000;

const DEFAULT_PLATFORM_KEY = META;

const PLATFORM_CONFIG = {
  [FACEBOOK]: {
    calendarLabelKey: FACEBOOK_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_FACEBOOK,
    eventColor: "#3b82f6",
  },
  [TIKTOK]: {
    calendarLabelKey: TIKTOK_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_TIKTOK,
    eventColor: "#000000",
  },
  [YOUTUBE]: {
    calendarLabelKey: YOUTUBE_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_YOUTUBE,
    eventColor: "#FF0000",
  },
  [INSTAGRAM]: {
    calendarLabelKey: INSTAGRAM_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_INSTAGRAM,
    eventColor: "#cc2366",
  },
  [LINKEDIN]: {
    calendarLabelKey: LINKEDIN_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_LINKEDIN,
    eventColor: "#0077B5",
  },
};

const toTitleCase = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const getPlatformConfig = (platformKey) =>
  PLATFORM_CONFIG[platformKey] ?? PLATFORM_CONFIG[DEFAULT_PLATFORM_KEY];

const getPlatformLabel = (platformKey, t, labelType) => {
  if (!platformKey) return "";

  const config = PLATFORM_CONFIG[platformKey];
  if (!config) return toTitleCase(platformKey);

  if (config.displayName) return config.displayName;

  const translationKey =
    labelType === "network" ? config.networkLabelKey : config.calendarLabelKey;

  return translationKey ? t(translationKey) : toTitleCase(platformKey);
};

const getPlatformEventColor = (platformKey) =>
  getPlatformConfig(platformKey).eventColor;

const STATUS_CONFIG_LOCAL = STATUS_CONFIG;

const getEventStatus = (statesByPlatform, t) => {
  const state = resolveCalendarEventStatus(statesByPlatform);
  const { labelKey, opacity } =
    STATUS_CONFIG_LOCAL[state] ?? STATUS_CONFIG_LOCAL[STATES.SCHEDULED];

  return {
    state,
    label: t(labelKey),
    opacity,
  };
};

const getCalendarTitleKey = (platformsData) => {
  if (!platformsData || typeof platformsData !== "object")
    return CALENDAR_POST_TITLE;

  const platformKeys = Object.keys(platformsData);
  const singlePlatform = platformKeys[0];

  if (
    platformKeys.length !== 1 ||
    ![FACEBOOK, INSTAGRAM].includes(singlePlatform)
  ) {
    return CALENDAR_POST_TITLE;
  }

  const mediaType = platformsData[singlePlatform]?.mediaType;
  if (!mediaType) return CALENDAR_POST_TITLE;

  const contentTypeMap = {
    [INSTAGRAM_CONTENT_TYPE_STORIES]: CALENDAR_STORIES_TITLE,
    [INSTAGRAM_CONTENT_TYPE_REELS]: CALENDAR_REELS_TITLE,
    [FACEBOOK_CONTENT_TYPE_STORIES]: CALENDAR_STORIES_TITLE,
    [FACEBOOK_CONTENT_TYPE_REELS]: CALENDAR_REELS_TITLE,
  };

  return contentTypeMap[mediaType] || CALENDAR_POST_TITLE;
};

const buildCalendarEvents = (scheduledContents, t) => {
  const calendarEvents = [];

  Object.entries(scheduledContents).forEach(([fallbackId, scheduledPost]) => {
    const platformKeys = scheduledPost.platforms;
    if (
      !platformKeys ||
      !Array.isArray(platformKeys) ||
      platformKeys.length === 0
    )
      return;

    const startDate = new Date(scheduledPost.scheduledDate);
    if (Number.isNaN(startDate.getTime())) return;

    const endDate = new Date(startDate.getTime() + EVENT_DURATION_MS);

    const isMultiPlatformPost = platformKeys.length > 1;
    const primaryPlatformKey = isMultiPlatformPost ? null : platformKeys[0];

    const statesByPlatform = scheduledPost.states;
    const publishIssue = classifyCalendarPublishIssue(statesByPlatform);
    const { label: statusLabel } = getEventStatus(statesByPlatform, t);

    const calendarPlatformLabel = getPlatformLabel(
      primaryPlatformKey,
      t,
      "calendar"
    );

    // Determinar el tipo de título basado en platformsData
    const calendarTitleKey = getCalendarTitleKey(scheduledPost.platformsData);

    const title = isMultiPlatformPost
      ? t(CALENDAR_MULTIPLE_POST_TITLE)
      : `${t(calendarTitleKey)} - ${calendarPlatformLabel}`;

    const networkLabel = isMultiPlatformPost
      ? t(CALENDAR_MULTIPLE_POST_TITLE)
      : getPlatformLabel(primaryPlatformKey, t, "network");

    calendarEvents.push({
      id: scheduledPost.scheduleID || fallbackId,
      title,
      start: startDate,
      end: endDate,
      platform: primaryPlatformKey,
      platforms: platformKeys,
      platformsData: scheduledPost.platformsData || {},
      states: statesByPlatform,
      originalData: scheduledPost,
      isMultiple: isMultiPlatformPost,
      networkLabel,
      statusLabel,
      publishIssue,
    });
  });

  return calendarEvents;
};

const getPeriodStartDate = (date, view) => {
  const byView = {
    month: startOfMonth,
    week: startOfDay,
    day: startOfDay,
  };
  return (byView[view] ?? ((d) => d))(date);
};

const isNavigatingToPastPeriod = (targetDate, view, today) => {
  return getPeriodStartDate(targetDate, view) < getPeriodStartDate(today, view);
};

const canGoToPreviousPeriod = (currentDate, view, today) => {
  return (
    getPeriodStartDate(currentDate, view) > getPeriodStartDate(today, view)
  );
};

const buildEventStyle = (overrides) => ({
  ...overrides,
  color: "white",
  borderRadius: "8px",
  border: "none",
  padding: "4px 6px",
  fontSize: "12px",
});

export const useModalCalendar = (
  modalOpen,
  { navigateToScheduledRequest = null, onNavigateToScheduledConsumed } = {}
) => {
  const dispatch = useDispatchApp();
  const { i18n, t } = useTranslationApp();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [view, setView] = useState("week");
  const [date, setDate] = useState(new Date());

  const anchorRef = useRef(null);
  const listenerRef = useRef(null);
  const prevModalOpenRef = useRef(false);

  const [scheduledContents, setScheduledContents] = useState({});

  const culture = useMemo(
    () => (i18n.language?.startsWith("es") ? "es" : "en"),
    [i18n.language]
  );

  const isPopoverOpen = useMemo(
    () => Boolean(anchorEl && selectedEvent),
    [anchorEl, selectedEvent]
  );

  const refreshScheduledContents = useCallback(
    async (showLoading = false) => {
      if (!modalOpen) return;

      if (showLoading) setIsLoading(true);

      try {
        const fetchedScheduledContents = await dispatch(
          startGetScheduledContents()
        );
        if (fetchedScheduledContents !== false) {
          setScheduledContents(fetchedScheduledContents || {});
        }
      } catch (error) {
        console.error("Error refreshing scheduled contents:", error);
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [modalOpen, dispatch]
  );

  useEffect(() => {
    refreshScheduledContents(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    setCalendarEvents(buildCalendarEvents(scheduledContents, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledContents, i18n.language]);

  useEffect(() => {
    if (!modalOpen) {
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
      }
      return;
    }

    dispatch(
      startListenerScheduledContents(
        (updatedContents) => {
          setScheduledContents(updatedContents || {});
        },
        (ref) => {
          listenerRef.current = ref;
        }
      )
    );

    return () => {
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
      }
    };
  }, [modalOpen, dispatch]);

  useEffect(() => {
    if (!modalOpen) {
      setAnchorEl(null);
      setSelectedEvent(null);
      setView("week");
      setDate(new Date());
      prevModalOpenRef.current = false;
      return;
    }

    const justOpened = !prevModalOpenRef.current;
    prevModalOpenRef.current = true;

    if (justOpened && !navigateToScheduledRequest?.date) {
      setDate(new Date());
    }
  }, [modalOpen, navigateToScheduledRequest]);

  useEffect(() => {
    if (!modalOpen || !navigateToScheduledRequest?.date) return;
    setDate(navigateToScheduledRequest.date);
    setView("week");
    onNavigateToScheduledConsumed?.();
  }, [modalOpen, navigateToScheduledRequest, onNavigateToScheduledConsumed]);

  const onClosePopover = useCallback(() => {
    setAnchorEl(null);
    setSelectedEvent(null);
  }, []);

  const onEventClick = useCallback((event, e) => {
    const eventElement =
      e?.currentTarget ||
      document.querySelector(`[data-event-id="${event.id}"]`);

    if (!eventElement || !anchorRef.current) return;

    const rect = eventElement.getBoundingClientRect();
    anchorRef.current.style.cssText = `
      position: fixed;
      top: ${rect.bottom}px;
      left: ${rect.left}px;
      width: ${rect.width || 200}px;
      height: 1px;
      visibility: hidden;
    `;

    setAnchorEl(anchorRef.current);
    setSelectedEvent(event);
  }, []);

  const onShowMore = useCallback((selectedDate) => {
    setView("day");
    setDate(selectedDate);
  }, []);

  const onViewChange = useCallback(
    (newView) => {
      setView(newView);

      const today = new Date();
      if (isNavigatingToPastPeriod(date, newView, today)) {
        setDate(getPeriodStartDate(today, newView));
      }
    },
    [date]
  );

  const onNavigate = useCallback(
    (newDateOrAction) => {
      const today = new Date();

      if (typeof newDateOrAction === "string") {
        if (
          newDateOrAction === "PREV" &&
          !canGoToPreviousPeriod(date, view, today)
        ) {
          return;
        }
        return;
      }

      const targetDate = newDateOrAction;

      if (view === "month") {
        const todayStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        const targetStart = new Date(
          targetDate.getFullYear(),
          targetDate.getMonth(),
          targetDate.getDate()
        );

        if (targetStart < todayStart) return;
      }

      if (isNavigatingToPastPeriod(targetDate, view, today)) {
        setDate(getPeriodStartDate(today, view));
        return;
      }

      setDate(targetDate);
    },
    [view, date]
  );

  const eventPropGetter = useCallback(
    (event) => {
      const isMultiPlatformPost = event.platforms.length > 1;

      const { opacity } = getEventStatus(event.states, t);

      let backgroundStyleFragment;
      if (isMultiPlatformPost) {
        backgroundStyleFragment = {
          background: GRADIENT_BLACK_COLOR,
          opacity,
        };
      } else {
        const platformKey =
          event.platforms[0] || event.platform || DEFAULT_PLATFORM_KEY;
        backgroundStyleFragment = {
          backgroundColor: getPlatformEventColor(platformKey),
          opacity,
        };
      }

      let classNames = "";

      if (event.publishIssue === "failure") {
        classNames = "calendar-event-publish--failure";
      } else if (event.publishIssue === "partial") {
        classNames = "calendar-event-publish--partial-failure";
      }

      return {
        className: classNames,
        style: buildEventStyle(backgroundStyleFragment),
      };
    },
    [t]
  );

  useEffect(() => {
    if (!isPopoverOpen) return;

    const onClickOutside = (e) => {
      const popover = document.querySelector('[role="tooltip"]');
      const eventElement = document.querySelector(
        `[data-event-id="${selectedEvent?.id}"]`
      );

      if (
        popover &&
        !popover.contains(e.target) &&
        eventElement &&
        !eventElement.contains(e.target)
      ) {
        onClosePopover();
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isPopoverOpen, selectedEvent, onClosePopover]);

  return {
    anchorEl,
    selectedEvent,
    events: calendarEvents,
    isLoading,
    view,
    date,
    culture,
    open: isPopoverOpen,
    anchorRef,
    onEventClick,
    onClosePopover,
    onShowMore,
    onViewChange,
    onNavigate,
    eventPropGetter,
    refreshScheduledContents,
  };
};
